import { useEffect, useState } from "react";
import { jobsApi } from "@/api";
import type { JobListRow } from "@/types";
import { Button } from "@/components/ui";

/** Inline job search picker, shared by the application review queue ("Find a
 *  job…" toggle) and the shell-job merge (auto-open, pre-seeded with the shell's
 *  name, filtered to real jobs). Searches GET /jobs/list by `q`. */
export function JobPicker({
  onPick,
  busy = false,
  initialQuery = "",
  autoOpen = false,
  filter,
  onCancel,
  placeholder = "Search job #, client, address…",
  triggerLabel = "Find a job…",
}: {
  onPick: (job: JobListRow) => void;
  busy?: boolean;
  initialQuery?: string;
  autoOpen?: boolean;
  /** Narrow the results (e.g. real jobs only). Applied at render, so an inline
   *  arrow is fine. */
  filter?: (job: JobListRow) => boolean;
  onCancel?: () => void;
  placeholder?: string;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(autoOpen);
  const [q, setQ] = useState(initialQuery);
  const [results, setResults] = useState<JobListRow[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    const t = setTimeout(() => {
      jobsApi
        .listJobs({ q: term, per_page: 8 })
        .then((res) => {
          if (!cancelled) setResults(res.items ?? []);
        })
        .catch(() => {
          if (!cancelled) setResults([]);
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, open]);

  function cancel() {
    if (autoOpen) {
      onCancel?.();
      return;
    }
    setOpen(false);
    setQ("");
  }

  if (!open) {
    return (
      <Button size="sm" variant="ghost" onClick={() => setOpen(true)}>
        {triggerLabel}
      </Button>
    );
  }

  const term = q.trim();
  const visible = filter ? results.filter(filter) : results;
  return (
    <div className="review-picker">
      <input
        className="leads__search review-picker__input"
        type="search"
        autoFocus
        placeholder={placeholder}
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {searching ? <p className="review-picker__hint">Searching…</p> : null}
      {!searching && term.length >= 2 && visible.length === 0 ? (
        <p className="review-picker__hint">No matching jobs found.</p>
      ) : null}
      {visible.length > 0 ? (
        <ul className="review-picker__results">
          {visible.map((job) => (
            <li key={job.id}>
              <button type="button" className="review-picker__result" disabled={busy} onClick={() => onPick(job)}>
                <span className="review-picker__result-num">#{job.job_number}</span>
                <span className="review-picker__result-name">{job.job_name || "—"}</span>
                <span className="review-picker__result-client">{job.client_display || job.postcode || "—"}</span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <button type="button" className="review-picker__cancel" onClick={cancel}>
        Cancel
      </button>
    </div>
  );
}
